import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { TaskEricInput } from '@/components/dashboard/agentic/TaskEricInput'
import type { DispatchResponse } from '@/components/dashboard/agentic/types'
import { fromDispatchResponse, type ReceiptVM } from '@/lib/receipts'

import { dockVisibleFor } from './dock-routes'
import { ReceiptsHost, type DockReceipt } from './ReceiptsHost'

/** Undone receipts linger briefly (parity with the old banner's 4s
    auto-dismiss), then leave the stack. */
const UNDONE_LINGER_MS = 4000
/** Newest-first; the dock never carries more than two receipts so the
    composer + stack can't dominate a 375px viewport (§4.3). */
const MAX_RECEIPTS = 2

/**
 * The command dock (DESIGN.md §4.3, plan PR-4 — wrap, don't rewrite):
 * ONE composer, docked at the bottom of every dock route, staged work
 * surviving navigation because the dock stays mounted and merely
 * display:none's itself elsewhere. TaskEricInput runs unchanged inside
 * the chrome; its executed results surface as §4.1 receipts UNDER the
 * composer via the `onExecuted` seam instead of the legacy inline
 * banner. Receipts are transient (cleared on navigation) — the durable
 * record is Activity (§5).
 */
export function CommandDock({ suppressed = false }: { suppressed?: boolean }) {
  const location = useLocation()
  // Suppression covers surfaces whose "own composer is open" state lives
  // in component state, not the URL (the thread workspace strips its
  // ?thread= param after consuming it). The URL check still covers the
  // first paint of a ?thread= deep link before that effect runs.
  const visible = !suppressed && dockVisibleFor(location.pathname, location.search)
  const [receipts, setReceipts] = useState<DockReceipt[]>([])

  const handleExecuted = useCallback((res: DispatchResponse) => {
    const vm = fromDispatchResponse(res, new Date().toISOString())
    if (!vm) return
    // `inline-*` ids are the composer's synthetic markers for fast-lane
    // commits without a real assistant_requests row — nothing to undo.
    const requestId =
      vm.requestId && vm.requestId.length > 0 && !vm.requestId.startsWith('inline-')
        ? vm.requestId
        : null
    // The inline fast-lane responses repeat the customer name as the
    // result label ("Booked Sarah Chen" / "Sarah Chen") — drop a sub the
    // title already contains.
    const sub = vm.sub && vm.title.includes(vm.sub) ? null : vm.sub
    setReceipts((prev) =>
      [
        {
          vm: { ...vm, requestId, sub },
          conflicts: res.conflicts ?? [],
          carryover: res.carryover ?? [],
        },
        ...prev,
      ].slice(0, MAX_RECEIPTS),
    )
  }, [])

  // Undone receipts show their struck state, then clear.
  const undoneTimers = useRef<number[]>([])
  const handleUndone = useCallback((vm: ReceiptVM) => {
    const id = window.setTimeout(() => {
      setReceipts((prev) => prev.filter((r) => r.vm.requestId !== vm.requestId))
    }, UNDONE_LINGER_MS)
    undoneTimers.current.push(id)
  }, [])
  useEffect(() => {
    const timers = undoneTimers.current
    return () => timers.forEach((id) => window.clearTimeout(id))
  }, [])

  // Receipts are moment-of-action artifacts — navigation clears them
  // (staged work inside the composer survives; that's component state).
  // Suppression clears too: the thread workspace opens on the SAME
  // pathname, and receipts surviving behind a hidden dock would
  // reappear stale minutes later. Pattern A: render-time snapshot swaps,
  // not effects.
  const [prevPath, setPrevPath] = useState(location.pathname)
  if (prevPath !== location.pathname) {
    setPrevPath(location.pathname)
    setReceipts([])
  }
  const [prevSuppressed, setPrevSuppressed] = useState(suppressed)
  if (suppressed !== prevSuppressed) {
    setPrevSuppressed(suppressed)
    if (suppressed) setReceipts([])
  }

  return (
    <div
      className={
        visible
          ? // Height governor (§4.3/§3.4): with interactive-widget=
            // resizes-content an open Android keyboard shrinks the layout
            // viewport (and dvh with it) — without a cap the composer's
            // tall internal panels could push the input pill off the top.
            // 96px headroom = TabBar (61) + breathing room; the focused
            // field auto-scrolls into view inside this container. Safe-
            // area pad matters at lg+ where the TabBar (which carries it
            // on phone) is hidden.
            'max-h-[calc(100dvh-96px)] overflow-y-auto overscroll-contain border-t border-border bg-background lg:pb-[env(safe-area-inset-bottom)]'
          : 'hidden'
      }
    >
      <div className="mx-auto w-full max-w-2xl px-3 pb-1 pt-2">
        <TaskEricInput hideHero micActive={visible} onExecuted={handleExecuted} />
      </div>
      <ReceiptsHost items={receipts} onUndone={handleUndone} />
    </div>
  )
}
